import React, { useEffect, useMemo, useState } from 'react';
import {
  AdminResearchInsightsStateDto,
  AdminUserDto,
  adminApi,
  isUnauthorized,
} from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminResearchInsightsScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

type SortKey = 'updatedAtUtc' | 'userId' | 'username' | 'stockSymbol' | 'id';

type InsightTab =
  | 'Business Operations'
  | 'Strengths'
  | 'Weaknesses'
  | 'Opportunities'
  | 'Threats'
  | 'Moats'
  | 'Stock'
  | 'Other';

const TABS: InsightTab[] = [
  'Business Operations',
  'Strengths',
  'Weaknesses',
  'Opportunities',
  'Threats',
  'Moats',
  'Stock',
  'Other',
];

const AdminResearchInsightsScreen: React.FC<AdminResearchInsightsScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);

  const colors = useMemo(
    () => ({
      card: theme.cardBg,
      border: theme.border,
      text: theme.text,
      subText: theme.subText,
      hover: theme.hoverBg,
      inputBg: theme.inputBg,
      primary: theme.primary,
      danger: theme.danger,
      radius: theme.radius,
    }),
    [theme]
  );

  const [items, setItems] = useState<AdminResearchInsightsStateDto[] | null>(null);
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  const [gridSearch, setGridSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'updatedAtUtc', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [criteriaDraft, setCriteriaDraft] = useState({ username: '', stockSymbol: '' });
  const [criteriaApplied, setCriteriaApplied] = useState(criteriaDraft);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<InsightTab>('Business Operations');
  const [previewState, setPreviewState] = useState<AdminResearchInsightsStateDto | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 34,
    borderRadius: colors.radius,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.inputBg,
    padding: '0 10px',
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 700,
  };

  const buttonStyle = (variant: 'primary' | 'neutral' | 'danger', disabled?: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: 34,
      padding: '0 12px',
      borderRadius: colors.radius,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.card,
      color: colors.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13,
      fontWeight: 800,
      opacity: disabled ? 0.6 : 1,
      whiteSpace: 'nowrap',
    };

    if (variant === 'primary') return { ...base, border: 'none', backgroundColor: colors.primary, color: '#fff' };
    if (variant === 'danger') return { ...base, border: 'none', backgroundColor: colors.danger, color: '#fff' };
    return base;
  };

  const normalize = (v: unknown): string => (v ?? '').toString().trim().toLowerCase();

  const userLookup = useMemo(() => {
    const map = new Map<number, string>();
    (users ?? []).forEach((u) => map.set(u.id, u.username));
    return map;
  }, [users]);

  const refresh = async (opts?: { clearSelection?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const [states, usersList] = await Promise.all([
        adminApi.listResearchInsightsStates(token),
        adminApi.listUsers(token),
      ]);
      setItems(states);
      setUsers(usersList);
      if (opts?.clearSelection) setSelectedIds(new Set());
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed loading research insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh({ clearSelection: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const applyCriteria = () => {
    setCriteriaApplied(criteriaDraft);
    setPage(1);
    setSelectedIds(new Set());
  };

  const clearCriteria = () => {
    const cleared = { username: '', stockSymbol: '' };
    setCriteriaDraft(cleared);
    setCriteriaApplied(cleared);
    setGridSearch('');
    setPage(1);
    setSelectedIds(new Set());
  };

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key: SortKey): string => {
    if (sort.key !== key) return '';
    return sort.dir === 'asc' ? ' ▲' : ' ▼';
  };

  const filteredItems = useMemo(() => {
    const list = items ?? [];
    const q = normalize(gridSearch);

    const usernameFilter = criteriaApplied.username.trim();
    const stockFilter = normalize(criteriaApplied.stockSymbol);

    const result = list.filter((x) => {
      if (usernameFilter.length > 0) {
        const resolvedUsername = (userLookup.get(x.userId) ?? x.username ?? '').toString();
        if (!normalize(resolvedUsername).includes(normalize(usernameFilter))) return false;
      }
      if (stockFilter.length > 0) {
        if (!normalize(x.stockSymbol).includes(stockFilter)) return false;
      }

      if (q.length > 0) {
        const haystack = [
          x.id,
          x.userId,
          userLookup.get(x.userId) ?? x.username,
          x.stockSymbol,
          x.updatedAtUtc,
        ]
          .map((v) => (v ?? '').toString().toLowerCase())
          .join(' | ');
        if (!haystack.includes(q)) return false;
      }

      return true;
    });

    const compare = (a: AdminResearchInsightsStateDto, b: AdminResearchInsightsStateDto): number => {
      const dir = sort.dir === 'asc' ? 1 : -1;

      const getVal = (x: AdminResearchInsightsStateDto): string | number => {
        if (sort.key === 'username') return (userLookup.get(x.userId) ?? x.username ?? '').toString().toLowerCase();
        if (sort.key === 'updatedAtUtc') {
          const d = new Date((x.updatedAtUtc ?? '') as string);
          return Number.isNaN(d.getTime()) ? 0 : d.getTime();
        }
        const v = (x as any)[sort.key];
        if (typeof v === 'number') return v;
        return (v ?? '').toString().toLowerCase();
      };

      const av = getVal(a);
      const bv = getVal(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return av.toString().localeCompare(bv.toString()) * dir;
    };

    return result.slice().sort(compare);
  }, [criteriaApplied.stockSymbol, criteriaApplied.username, gridSearch, items, sort.dir, sort.key, userLookup]);

  const usernameOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.username ?? '').toString().trim())
      .filter((u) => u.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const total = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(Math.max(1, page), totalPages);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
  }, [page, pageSafe]);

  const pageItems = useMemo(() => {
    const start = (pageSafe - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, pageSafe, pageSize]);

  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = pageItems.every((x) => next.has(x.id));
      pageItems.forEach((x) => {
        if (allSelected) next.delete(x.id);
        else next.add(x.id);
      });
      return next;
    });
  };

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const ok = window.confirm(`Delete ${selectedIds.size} item(s)?`);
    if (!ok) return;

    setLoading(true);
    setError(null);
    try {
      await adminApi.deleteResearchInsightsStates(token, { ids: Array.from(selectedIds) });
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed deleting research insights');
      setLoading(false);
    }
  };

  const openPreview = async (row: AdminResearchInsightsStateDto) => {
    setPreviewOpen(true);
    setPreviewTab('Business Operations');
    setPreviewBusy(true);
    setPreviewState(null);

    try {
      const res = await adminApi.getResearchInsightsState(token, row.userId, row.stockSymbol);
      setPreviewState({
        ...row,
        businessOperationsHtml: res.businessOperationsHtml,
        strengthsHtml: res.strengthsHtml,
        weaknessesHtml: res.weaknessesHtml,
        opportunitiesHtml: res.opportunitiesHtml,
        threatsHtml: res.threatsHtml,
        moatsHtml: res.moatsHtml,
        stockHtml: res.stockHtml,
        otherHtml: res.otherHtml,
        updatedAtUtc: res.updatedAtUtc ?? row.updatedAtUtc,
      });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setPreviewState(row);
    } finally {
      setPreviewBusy(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewState(null);
  };

  const renderHtml = (html: string) => {
    return (
      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: colors.radius,
          padding: 12,
          backgroundColor: colors.card,
          color: colors.text,
          minHeight: 220,
          overflow: 'auto',
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  const htmlForTab = (tab: InsightTab, s: AdminResearchInsightsStateDto | null): string => {
    if (!s) return '';
    switch (tab) {
      case 'Business Operations':
        return (s.businessOperationsHtml ?? '').toString();
      case 'Strengths':
        return (s.strengthsHtml ?? '').toString();
      case 'Weaknesses':
        return (s.weaknessesHtml ?? '').toString();
      case 'Opportunities':
        return (s.opportunitiesHtml ?? '').toString();
      case 'Threats':
        return (s.threatsHtml ?? '').toString();
      case 'Moats':
        return (s.moatsHtml ?? '').toString();
      case 'Stock':
        return (s.stockHtml ?? '').toString();
      case 'Other':
        return (s.otherHtml ?? '').toString();
      default:
        return '';
    }
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div
        style={{
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.radius,
          padding: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 900, color: colors.text }}>Research Insights</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button style={buttonStyle('neutral', loading)} onClick={() => refresh({ clearSelection: true })}>
              Refresh
            </button>
            <button style={buttonStyle('danger', loading || selectedIds.size === 0)} disabled={loading || selectedIds.size === 0} onClick={deleteSelected}>
              Delete Selected
            </button>
          </div>
        </div>

        {error ? (
          <div style={{ marginTop: 10, color: colors.danger, fontWeight: 800, fontSize: 13 }}>{error}</div>
        ) : null}

        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Username</div>
            <input
              value={criteriaDraft.username}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, username: e.target.value }))}
              placeholder="Select or type..."
              list="admin-research-insights-username-options"
              style={inputStyle}
            />
            <datalist id="admin-research-insights-username-options">
              {usernameOptions.map((u) => (
                <option key={u} value={u} />
              ))}
            </datalist>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Stock Symbol</div>
            <input
              value={criteriaDraft.stockSymbol}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, stockSymbol: e.target.value }))}
              placeholder="e.g. AAPL"
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Search</div>
            <input value={gridSearch} onChange={(e) => setGridSearch(e.target.value)} placeholder="Search" style={inputStyle} />
          </div>
        </div>

        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={buttonStyle('primary', loading)} disabled={loading} onClick={applyCriteria}>
            Apply
          </button>
          <button style={buttonStyle('neutral', loading)} disabled={loading} onClick={clearCriteria}>
            Clear
          </button>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ color: colors.subText, fontWeight: 800, fontSize: 13 }}>Page size</div>
            <select
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value);
                setPageSize(Number.isFinite(next) ? next : 10);
                setPage(1);
              }}
              style={{ ...inputStyle, width: 90, padding: '0 8px' }}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div
        style={{
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.radius,
          overflow: 'hidden',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: colors.card, borderBottom: `1px solid ${colors.border}` }}>
                <th style={{ textAlign: 'left', padding: 10, width: 42 }}>
                  <input
                    type="checkbox"
                    checked={pageItems.length > 0 && pageItems.every((x) => selectedIds.has(x.id))}
                    onChange={toggleAllOnPage}
                  />
                </th>
                <th
                  onClick={() => toggleSort('id')}
                  style={{ textAlign: 'left', padding: 10, cursor: 'pointer', color: colors.text, fontWeight: 900, fontSize: 13 }}
                >
                  Id{sortIndicator('id')}
                </th>
                <th
                  onClick={() => toggleSort('userId')}
                  style={{ textAlign: 'left', padding: 10, cursor: 'pointer', color: colors.text, fontWeight: 900, fontSize: 13 }}
                >
                  User Id{sortIndicator('userId')}
                </th>
                <th
                  onClick={() => toggleSort('username')}
                  style={{ textAlign: 'left', padding: 10, cursor: 'pointer', color: colors.text, fontWeight: 900, fontSize: 13 }}
                >
                  Username{sortIndicator('username')}
                </th>
                <th
                  onClick={() => toggleSort('stockSymbol')}
                  style={{ textAlign: 'left', padding: 10, cursor: 'pointer', color: colors.text, fontWeight: 900, fontSize: 13 }}
                >
                  Stock{sortIndicator('stockSymbol')}
                </th>
                <th
                  onClick={() => toggleSort('updatedAtUtc')}
                  style={{ textAlign: 'left', padding: 10, cursor: 'pointer', color: colors.text, fontWeight: 900, fontSize: 13 }}
                >
                  Updated{sortIndicator('updatedAtUtc')}
                </th>
                <th style={{ textAlign: 'right', padding: 10, width: 120, color: colors.text, fontWeight: 900, fontSize: 13 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ padding: 14, color: colors.subText, fontWeight: 800 }}>
                    Loading…
                  </td>
                </tr>
              ) : pageItems.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 14, color: colors.subText, fontWeight: 800 }}>
                    No items
                  </td>
                </tr>
              ) : (
                pageItems.map((x) => (
                  <tr key={x.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                    <td style={{ padding: 10 }}>
                      <input type="checkbox" checked={selectedIds.has(x.id)} onChange={() => toggleOne(x.id)} />
                    </td>
                    <td style={{ padding: 10, color: colors.text, fontWeight: 800, fontSize: 13 }}>{x.id}</td>
                    <td style={{ padding: 10, color: colors.text, fontWeight: 800, fontSize: 13 }}>{x.userId}</td>
                    <td style={{ padding: 10, color: colors.text, fontWeight: 800, fontSize: 13 }}>
                      {userLookup.get(x.userId) ?? x.username ?? '—'}
                    </td>
                    <td style={{ padding: 10, color: colors.text, fontWeight: 800, fontSize: 13 }}>{x.stockSymbol}</td>
                    <td style={{ padding: 10, color: colors.subText, fontWeight: 800, fontSize: 13 }}>
                      {x.updatedAtUtc ? new Date(x.updatedAtUtc).toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: 10, textAlign: 'right' }}>
                      <button style={buttonStyle('neutral', loading)} disabled={loading} onClick={() => void openPreview(x)}>
                        Preview
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 12,
            borderTop: `1px solid ${colors.border}`,
            color: colors.subText,
            fontWeight: 800,
            fontSize: 13,
          }}
        >
          <div>
            {total === 0 ? '0' : (pageSafe - 1) * pageSize + 1}–{Math.min(pageSafe * pageSize, total)} of {total}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={buttonStyle('neutral', loading || pageSafe <= 1)} disabled={loading || pageSafe <= 1} onClick={() => setPage(1)}>
              First
            </button>
            <button
              style={buttonStyle('neutral', loading || pageSafe <= 1)}
              disabled={loading || pageSafe <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <div style={{ color: colors.text }}>Page {pageSafe} / {totalPages}</div>
            <button
              style={buttonStyle('neutral', loading || pageSafe >= totalPages)}
              disabled={loading || pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
            <button
              style={buttonStyle('neutral', loading || pageSafe >= totalPages)}
              disabled={loading || pageSafe >= totalPages}
              onClick={() => setPage(totalPages)}
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {previewOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePreview();
          }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: 'min(1050px, 96vw)',
              maxHeight: '90vh',
              overflow: 'auto',
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: colors.radius,
              padding: 14,
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ fontWeight: 900, color: colors.text }}>
                Preview — {previewState?.stockSymbol ?? ''} (User {previewState?.userId ?? ''})
              </div>
              <button style={buttonStyle('neutral')} onClick={closePreview}>
                Close
              </button>
            </div>

            {previewBusy ? (
              <div style={{ marginTop: 12, color: colors.subText, fontWeight: 800 }}>Loading…</div>
            ) : null}

            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {TABS.map((t) => {
                const active = t === previewTab;
                return (
                  <button
                    key={t}
                    style={{
                      ...buttonStyle(active ? 'primary' : 'neutral'),
                      height: 32,
                      padding: '0 10px',
                    }}
                    onClick={() => setPreviewTab(t)}
                  >
                    {t}
                  </button>
                );
              })}
            </div>

            <div style={{ marginTop: 12 }}>
              {renderHtml(htmlForTab(previewTab, previewState))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminResearchInsightsScreen;
